import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import AdminDashboard from "../AdminDashboard/admindash";
import ConfirmModal from "../AdminDashboard/ConfirmModal";
import {
  Plus,
  Search,
  Filter,
  MapPin,
  Hotel,
  DollarSign,
  Trash2,
  Edit3,
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  XCircle,
  TrendingUp,
  Loader2,
  Building2,
  List,
  Home
} from "lucide-react";
import { toast } from "react-toastify";
import API_URL from "../../config";


// Modern Image Slider Component
function ImageSlider({ images, name }) {
  const [index, setIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    if (!images || images.length <= 1 || isHovered) return;
    const interval = setInterval(() => {
      setIndex((i) => (i === images.length - 1 ? 0 : i + 1));
    }, 4000);
    return () => clearInterval(interval);
  }, [images, isHovered]);

  if (!images || images.length === 0) {
    return (
      <div className="w-full h-56 bg-gray-100 flex flex-col items-center justify-center text-gray-400 gap-2">
        <Hotel size={32} />
        <span className="text-[10px] font-black uppercase tracking-widest">No Property Asset</span>
      </div>
    );
  }

  return (
    <div
      className="relative w-full h-56 overflow-hidden"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <img
        src={images[index]}
        alt={`${name} ${index + 1}`}
        className="w-full h-full object-cover transition-transform duration-700 hover:scale-110"
        onError={(e) => {
          e.target.src = "https://images.unsplash.com/photo-1566073771259-6a8506099945?q=80&w=1000&auto=format&fit=crop";
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-60" />

      {isHovered && images.length > 1 && (
        <>
          <button
            onClick={(e) => { e.stopPropagation(); setIndex((i) => (i === 0 ? images.length - 1 : i - 1)); }}
            className="absolute left-2 top-1/2 -translate-y-1/2 p-2 bg-white/20 backdrop-blur-md rounded-full text-white hover:bg-white/40 transition-all"
          >
            <ChevronLeft size={16} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); setIndex((i) => (i === images.length - 1 ? 0 : i + 1)); }}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-white/20 backdrop-blur-md rounded-full text-white hover:bg-white/40 transition-all"
          >
            <ChevronRight size={16} />
          </button>
        </>
      )}

      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5 px-2 py-1 bg-black/20 backdrop-blur-sm rounded-full">
        {images.map((_, i) => (
          <div
            key={i}
            className={`h-1 rounded-full transition-all duration-300 ${i === index ? "w-4 bg-white" : "w-1 bg-white/50"}`}
          />
        ))}
      </div>
    </div>
  );
}

function Accommodationpage() {
  const [accommodations, setAccommodations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState("All");
  const [filterAvailability, setFilterAvailability] = useState("All");
  const [filterLocation, setFilterLocation] = useState("All");
  const [priceRange, setPriceRange] = useState([0, 500000]);
  const [stats, setStats] = useState({ total: 0, available: 0, unavailable: 0 });

  // Deletion Modal State
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [accToDelete, setAccToDelete] = useState(null);

  const navigate = useNavigate();

  const fetchAccommodations = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(`${API_URL}/accommodation`);
      const data = response.data;
      if (data.accommodations) {
        setAccommodations(data.accommodations);
        const total = data.accommodations.length;
        const available = data.accommodations.filter(acc => acc.availability).length;
        setStats({ total, available, unavailable: total - available });
      }
    } catch (err) {
      console.error("Error fetching accommodations:", err);
      toast.error("Failed to load property inventory.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAccommodations();
  }, []);

  const handleDeleteClick = (acc) => {
    setAccToDelete(acc);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!accToDelete) return;
    try {
      const res = await axios.delete(`${API_URL}/accommodation/${accToDelete._id}`);
      if (res.status === 200) {
        toast.success("Property asset permanently removed.");
        setAccommodations((prev) => prev.filter((acc) => acc._id !== accToDelete._id));
        setStats(prev => ({
          total: prev.total - 1,
          available: prev.available - (accToDelete.availability ? 1 : 0),
          unavailable: prev.unavailable - (accToDelete.availability ? 0 : 1)
        }));
      }
    } catch (err) {
      console.error("Error deleting accommodation:", err);
      toast.error("Failed to delete property asset.");
    } finally {
      setAccToDelete(null);
    }
  };

  const sriLankaCities = [
    "All", "Colombo", "Kandy", "Galle", "Jaffna", "Negombo", "Trincomalee",
    "Anuradhapura", "Nuwara Eliya", "Matara", "Ella", "Unawatuna", "Hikkaduwa"
  ];

  const filteredAccommodations = accommodations.filter((acc) => {
    const matchesSearch =
      acc.accommodationName.toLowerCase().includes(search.toLowerCase()) ||
      acc.description.toLowerCase().includes(search.toLowerCase());

    const matchesType = filterType === "All" || acc.type.toLowerCase() === filterType.toLowerCase();
    const matchesAvailability = filterAvailability === "All" || (filterAvailability === "Available" && acc.availability) || (filterAvailability === "Unavailable" && !acc.availability);
    const matchesLocation = filterLocation === "All" || acc.location.toLowerCase() === filterLocation.toLowerCase();
    const matchesPrice = acc.pricePerNight >= priceRange[0] && acc.pricePerNight <= priceRange[1];

    return matchesSearch && matchesType && matchesAvailability && matchesLocation && matchesPrice;
  });

  return (
    <AdminDashboard>
      <div className="font-sans pb-10">
        {/* Header & New Property Trigger */}
        <div className="mb-10 flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div>
            <h1 className="text-3xl font-black text-sky-950 tracking-tight flex items-center gap-3">
              <Building2 className="text-sky-500" size={32} strokeWidth={2.5} />
              Property Inventory Manager
            </h1>
            <p className="text-sm text-gray-400 font-bold mt-1">Full control over your global property listings and availability tags.</p>
          </div>
          <button
            onClick={() => navigate("/insertaccommodation")}
            className="group flex items-center gap-2 px-6 py-4 bg-sky-600 text-white font-black rounded-2xl hover:bg-sky-700 transition-all shadow-xl shadow-sky-600/20 active:scale-95"
          >
            <Plus size={20} className="group-hover:rotate-90 transition-transform duration-300" />
            <span className="uppercase tracking-widest text-xs">Onboard New Property</span>
          </button>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-10">
          <div className="bg-white p-6 rounded-[2rem] border border-gray-100 shadow-sm flex items-center gap-5 transition-transform hover:scale-[1.02]">
            <div className="w-14 h-14 bg-sky-50 rounded-2xl flex items-center justify-center text-sky-600 shadow-inner">
              <Home size={28} />
            </div>
            <div>
              <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-0.5">Total Listings</p>
              <h3 className="text-2xl font-black text-sky-950 leading-none">{stats.total}</h3>
            </div>
          </div>

          <div className="bg-white p-6 rounded-[2rem] border border-gray-100 shadow-sm flex items-center gap-5 transition-transform hover:scale-[1.02]">
            <div className="w-14 h-14 bg-emerald-50 rounded-2xl flex items-center justify-center text-emerald-600 shadow-inner">
              <CheckCircle2 size={28} />
            </div>
            <div>
              <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-0.5">Live & Bookable</p>
              <h3 className="text-2xl font-black text-sky-950 leading-none">{stats.available}</h3>
            </div>
          </div>

          <div className="bg-white p-6 rounded-[2rem] border border-gray-100 shadow-sm flex items-center gap-5 transition-transform hover:scale-[1.02]">
            <div className="w-14 h-14 bg-rose-50 rounded-2xl flex items-center justify-center text-rose-600 shadow-inner">
              <XCircle size={28} />
            </div>
            <div>
              <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-0.5">Paused Listings</p>
              <h3 className="text-2xl font-black text-sky-950 leading-none">{stats.unavailable}</h3>
            </div>
          </div>

          <div className="bg-white p-6 rounded-[2rem] border border-gray-100 shadow-sm flex items-center gap-5 transition-transform hover:scale-[1.02]">
            <div className="w-14 h-14 bg-amber-50 rounded-2xl flex items-center justify-center text-amber-600 shadow-inner">
              <TrendingUp size={28} />
            </div>
            <div>
              <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-0.5">Inventory Quality</p>
              <h3 className="text-2xl font-black text-sky-950 leading-none">Premium</h3>
            </div>
          </div>
        </div>

        {/* Filters Section */}
        <div className="bg-white rounded-[2.5rem] border border-gray-100 p-8 shadow-sm mb-10 overflow-hidden relative">
          <div className="absolute top-0 right-0 w-32 h-32 bg-sky-50/50 rounded-bl-full -mr-16 -mt-16"></div>

          <div className="flex items-center gap-2 mb-8 border-l-4 border-sky-500 pl-4">
            <Filter size={18} className="text-sky-600" />
            <h3 className="text-xs font-black text-sky-950 uppercase tracking-widest">Inventory Filters</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="space-y-2">
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Universal Search</label>
              <div className="relative group">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300 group-focus-within:text-sky-500 transition-colors" size={18} />
                <input
                  type="text"
                  placeholder="Find property..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-12 pr-4 py-3.5 bg-gray-50/50 border border-gray-100 rounded-xl focus:bg-white focus:ring-4 focus:ring-sky-500/5 focus:border-sky-400 transition-all font-bold text-sky-950 outline-none text-sm placeholder:text-gray-300"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Property Hub</label>
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="w-full px-4 py-3.5 bg-gray-50/50 border border-gray-100 rounded-xl focus:bg-white focus:ring-4 focus:ring-sky-500/5 focus:border-sky-400 transition-all font-bold text-sky-950 outline-none appearance-none text-sm"
              >
                <option value="All">All Property Types</option>
                <option value="Hotel">Hotel</option>
                <option value="Villa">Villa</option>
                <option value="Resort">Resort</option>
                <option value="Apartment">Apartment</option>
                <option value="Guest House">Guest House</option>
              </select>
            </div>

            <div className="space-y-2">
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Live Status</label>
              <select
                value={filterAvailability}
                onChange={(e) => setFilterAvailability(e.target.value)}
                className="w-full px-4 py-3.5 bg-gray-50/50 border border-gray-100 rounded-xl focus:bg-white focus:ring-4 focus:ring-sky-500/5 focus:border-sky-400 transition-all font-bold text-sky-950 outline-none appearance-none text-sm"
              >
                <option value="All">Every Status</option>
                <option value="Available">Online (Available)</option>
                <option value="Unavailable">Offline (Paused)</option>
              </select>
            </div>

            <div className="space-y-2">
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Region Hub</label>
              <select
                value={filterLocation}
                onChange={(e) => setFilterLocation(e.target.value)}
                className="w-full px-4 py-3.5 bg-gray-50/50 border border-gray-100 rounded-xl focus:bg-white focus:ring-4 focus:ring-sky-500/5 focus:border-sky-400 transition-all font-bold text-sky-950 outline-none appearance-none text-sm"
              >
                {sriLankaCities.map((city) => (
                  <option key={city} value={city}>{city}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="mt-8 pt-6 border-t border-gray-50">
            <div className="flex items-center justify-between mb-2">
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">
                Budget Threshold: <span className="text-sky-600 font-black tracking-tight ml-2">LKR {priceRange[0].toLocaleString()} — LKR {priceRange[1].toLocaleString()}</span>
              </label>
              <button
                onClick={() => setPriceRange([0, 500000])}
                className="text-[9px] font-black uppercase text-sky-400 hover:text-sky-600 transition-colors"
              >
                Reset Threshold
              </button>
            </div>
            <div className="grid grid-cols-2 gap-8 items-center">
              <input
                type="range"
                min="0"
                max="250000"
                step="2500"
                value={priceRange[0]}
                onChange={(e) => setPriceRange([+e.target.value, priceRange[1]])}
                className="h-1.5 bg-sky-50 rounded-full appearance-none cursor-pointer accent-sky-500"
              />
              <input
                type="range"
                min="250000"
                max="500000"
                step="5000"
                value={priceRange[1]}
                onChange={(e) => setPriceRange([priceRange[0], +e.target.value])}
                className="h-1.5 bg-sky-50 rounded-full appearance-none cursor-pointer accent-sky-500"
              />
            </div>
          </div>
        </div>

        {/* Results Info */}
        <div className="mb-6 flex items-center gap-3">
          <div className="h-px bg-gray-100 flex-grow" />
          <span className="text-[10px] font-black text-gray-300 uppercase tracking-[0.2em]">{filteredAccommodations.length} Units Currently Synchronized</span>
          <div className="h-px bg-gray-100 flex-grow" />
        </div>

        {/* Grid Content */}
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-24 gap-4">
            <Loader2 className="animate-spin text-sky-500" size={48} />
            <p className="text-[10px] font-black uppercase tracking-widest text-sky-900/40">Fetching Inventory Data...</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredAccommodations.length > 0 ? (
              filteredAccommodations.map((acc) => (
                <div key={acc._id} className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 overflow-hidden group hover:shadow-2xl hover:shadow-sky-900/5 transition-all duration-500 flex flex-col relative">

                  {/* Image & Badges */}
                  <div className="relative h-56">
                    <ImageSlider images={acc.images} name={acc.accommodationName} />
                    <div className="absolute top-4 left-4 flex gap-2">
                      <span className={`px-3 py-1.5 text-[8px] font-black uppercase tracking-widest rounded-full backdrop-blur-md shadow-xl ${acc.availability ? "bg-emerald-500/90 text-white" : "bg-rose-500/90 text-white"}`}>
                        {acc.availability ? "Available" : "Paused"}
                      </span>
                      <span className="px-3 py-1.5 text-[8px] font-black uppercase tracking-widest rounded-full backdrop-blur-md bg-white/90 text-sky-600 shadow-xl">
                        {acc.type}
                      </span>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-8 flex flex-col flex-grow">
                    <div className="flex items-center gap-2 text-sky-600/60 mb-2 font-black uppercase text-[8px] tracking-[0.15em]">
                      <List size={10} strokeWidth={3} />
                      <span>{acc.accommodationId}</span>
                    </div>

                    <h3 className="text-xl font-black text-sky-950 mb-3 group-hover:text-sky-600 transition-colors leading-tight line-clamp-1">{acc.accommodationName}</h3>

                    <div className="space-y-3 mb-6 flex-grow">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-sky-50 rounded-xl flex items-center justify-center text-sky-600">
                          <MapPin size={14} strokeWidth={3} />
                        </div>
                        <p className="text-[10px] font-bold text-gray-500 truncate">{acc.location}</p>
                      </div>

                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-emerald-50 rounded-xl flex items-center justify-center text-emerald-600">
                          <DollarSign size={14} strokeWidth={3} />
                        </div>
                        <div className="flex items-baseline gap-1">
                          <span className="text-sm font-black text-sky-950">{acc.pricePerNight.toLocaleString()}</span>
                          <span className="text-[8px] font-black text-emerald-600 uppercase tracking-widest">{acc.currency || "LKR"}</span>
                        </div>
                      </div>
                    </div>

                    <p className="text-gray-400 text-[10px] font-bold leading-relaxed line-clamp-2 h-10 mb-6 border-l-2 border-gray-100 pl-4 py-1 italic">
                      "{acc.description}"
                    </p>

                    <div className="pt-6 border-t border-gray-50 flex gap-3">
                      <button
                        onClick={() => navigate(`/update-accommodation/${acc._id}`)}
                        className="flex-1 py-3.5 bg-gray-50 text-sky-950 font-black rounded-2xl hover:bg-sky-100 transition-all flex items-center justify-center gap-2 active:scale-95 group/btn"
                      >
                        <Edit3 size={14} className="text-sky-400 group-hover/btn:text-sky-600 transition-colors" />
                        <span className="text-[10px] uppercase tracking-widest">Update</span>
                      </button>
                      <button
                        onClick={() => handleDeleteClick(acc)}
                        className="w-14 h-12.5 bg-rose-50 text-rose-500 rounded-2xl hover:bg-rose-100 transition-all flex items-center justify-center active:scale-95 group/del"
                      >
                        <Trash2 size={18} className="group-hover/del:scale-110 transition-transform" />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="col-span-full py-20 flex flex-col items-center justify-center bg-white rounded-[3rem] border border-dashed border-gray-200 gap-4">
                <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center text-gray-200">
                  <Home size={40} />
                </div>
                <div className="text-center">
                  <h3 className="text-lg font-black text-sky-950">No Property Assets Registered</h3>
                  <p className="text-xs text-gray-400 font-bold mt-1">Adjust your inventory filters or onboard a new property asset.</p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Confirmation Modal */}
      <ConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={confirmDelete}
        title="Destroy Property Asset"
        message={`This will permanently remove "${accToDelete?.accommodationName}" from your global inventory and cancel all associated data. This action is irreversible. Complete destruction?`}
        confirmText="Yes, Permanently Destroy"
      />
    </AdminDashboard>
  );
}

export default Accommodationpage;
